/**
 * Shared presentational component for the compact job card.
 * Used by both the static SRP shell (props only, read-only) and the interactive tree
 * (HitCompact passes same props from useHitShared). One component, same styling;
 * only the attached handlers change (filter links, save, menu when interactive).
 */
import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Icon } from '../icons/Icon';
import { Link } from '../Link';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { MatchDonut } from '../MatchDonut';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { isDark } from './utils/brandColorUtils';
import { getApplicationStateButtonStateForStatic } from '../application-state/utils';
import { STATE_BUTTON_COLOR_CLASSES, STATE_ICON_COLOR_CLASSES, getStateLabelClass } from '../application-state/state-classes';
import {
  COMPACT_TITLE_LINK_CLASS_MOBILE,
  COMPACT_TITLE_SUBTITLE_CLASS_MOBILE,
} from './compactTitleClasses';
import type { HitCompactViewProps } from './HitCompactViewProps';
import type { MatchScoreBreakdown } from '../../utils/data/calculate-job-match-score';

const DETAILS_BTN_STYLE_READONLY = {
  backgroundColor: '#bbf7d0',
  color: '#111827',
  border: 'none' as const,
};

function getDetailsButtonStyle(props: HitCompactViewProps): React.CSSProperties {
  if (props.useBrandColour && props.brandColour) {
    return {
      backgroundColor: props.brandColour,
      color: isDark(props.brandColour) ? '#fff' : '#111827',
      border: 'none',
    };
  }
  return DETAILS_BTN_STYLE_READONLY;
}

function HitCompactViewInner(props: HitCompactViewProps): React.ReactElement {
  if (props.staticHitStrings) {
    return <HitCompactViewStatic {...props} />;
  }
  return <HitCompactViewWithIntl {...props} />;
}

export const HitCompactView = React.memo(HitCompactViewInner);

function HitCompactViewStatic(props: HitCompactViewProps & { staticHitStrings: NonNullable<HitCompactViewProps['staticHitStrings']> }): React.ReactElement {
  const detailsBtnStyle = getDetailsButtonStyle(props);
  const salaryDisplay = props.salaryLabel || props.staticHitStrings.salaryNotSpecified;
  const detailsLabel = props.staticHitStrings.detailsLabel;
  const { singleLocation } = props;
  const isSaved = props.isSaved === true;
  const isApplied = props.isApplied === true;
  const saveButtonStyle = isSaved
    ? {
        backgroundColor: props.useBrandColour && props.brandColour ? props.brandColour : '#bbf7d0',
        borderColor: props.useBrandColour && props.brandColour ? props.brandColour : '#bbf7d0',
      }
    : {};
  const saveIconColor = isSaved && (props.useBrandColour && props.brandColour)
    ? (isDark(props.brandColour) ? '#fff' : '#111827')
    : '#111827';
  const stateButtonState = getApplicationStateButtonStateForStatic(props.currentApplicationState ?? null, isApplied);
  const stateButtonColorClasses = STATE_BUTTON_COLOR_CLASSES[stateButtonState?.color ?? 'default'];
  const stateIconColorClasses = STATE_ICON_COLOR_CLASSES[stateButtonState?.color ?? 'default'];
  return (
    <article
      id={props.articleId}
      className="job-listing hit-card"
      title={props.jobTitle}
      aria-labelledby={props.titleId}
      style={{
        ...props.leftBorderStyle,
        position: 'relative',
        contentVisibility: 'auto',
        containIntrinsicSize: '1px 150px',
        contain: 'layout style paint',
      }}
    >
      <div className="hit-card__logo-mobile">
        <CompanyLogo
          companyName={props.companyId || ''}
          alt={props.companyId || ''}
          isTyomarkkinatori={props.isTyomarkkinatori}
          size={56}
          loading={props.index < 3 ? 'eager' : 'lazy'}
        />
        {props.companyId ? (
          <span className="hit-card__company-mobile">
            <CompanyName
              name={props.companyNameDisplay ?? props.companyId}
              className="font-bold text-xs md:text-sm text-gray-700 dark:text-gray-300"
            />
          </span>
        ) : null}
      </div>
      <div className="hit-card__main">
        <div className="hit-card__mobile-col">
          <div className="hit-card__title-block">
            <h3 id={props.titleId} className="sr-only">
              {props.jobTitle}
            </h3>
            <Link href={props.jobUrl} className={`${COMPACT_TITLE_LINK_CLASS_MOBILE} mb-0.5`}>
              {fixMojibake(props.jobTitle)}
            </Link>
            {props.rawTitleSubtitle ? (
              <div className={COMPACT_TITLE_SUBTITLE_CLASS_MOBILE}>
                {fixMojibake(props.rawTitleSubtitle)}
              </div>
            ) : null}
              {props.isToolsPage && props.staticHitStrings?.applicationStateSelector && (() => {
                const sel = props.staticHitStrings.applicationStateSelector!;
                const stateVal = props.currentApplicationState ?? '';
                const stateColorClass = getStateLabelClass(props.currentApplicationState, isApplied);
                return (
                  <div className="hit-state-row">
                    <label className="hit-state-label">{sel.label}</label>
                    <select
                      value={stateVal}
                      disabled
                      aria-readonly
                      className={`text-xs font-medium px-2.5 py-1.5 rounded border transition-all duration-200 appearance-none bg-white dark:bg-gray-800 cursor-default ${stateColorClass}`}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.25rem center',
                        backgroundSize: '1em 1em',
                        paddingRight: '1.75rem',
                      }}
                    >
                      <option value="">{sel.none}</option>
                      <option value="interviewing">{sel.interviewing}</option>
                      <option value="offer">{sel.offer}</option>
                      <option value="rejected">{sel.rejected}</option>
                      <option value="hired">{sel.hired}</option>
                    </select>
                  </div>
                );
              })()}
          </div>
          <div className="hit-pills-grid">
            {singleLocation.display ? (
              <span className="hit-pill-mobile hit-pill-mobile--green">
                <Icon name="mapPin" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">{fixMojibake(singleLocation.display)}</span>
              </span>
            ) : null}
            {props.remoteness ? (
              <span className="hit-pill-mobile hit-pill-mobile--blue hit-pill-mobile--w-auto">
                <Icon name="wifi" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">{fixMojibake(props.remoteness)}</span>
              </span>
            ) : null}
            {props.jobType ? (
              <span className="hit-pill-mobile hit-pill-mobile--purple hit-pill-mobile--w-auto">
                <Icon name="briefcase" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">{fixMojibake(props.jobType)}</span>
              </span>
            ) : null}
            {props.normalisedRoleLevel ? (
              <span className="hit-pill-mobile--orange-md-only">
                <span className="hit-pill__center">{fixMojibake(props.normalisedRoleLevel)}</span>
              </span>
            ) : null}
            {props.requiredLanguages?.length ? (
              <span className="hit-pill-mobile hit-pill-mobile--indigo hit-pill-mobile--w-auto">
                <Icon name="globeAlt" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">
                  {props.requiredLanguages.slice(0, 2).map((lang, idx) => (
                    <span key={lang + idx}>
                      {fixMojibake(lang)}
                      {idx < Math.min(props.requiredLanguages?.length ?? 0, 2) - 1 && ', '}
                    </span>
                  ))}
                  {(props.requiredLanguages?.length ?? 0) > 2 && ` +${(props.requiredLanguages?.length ?? 0) - 2}`}
                </span>
              </span>
            ) : null}
            <span className="hit-pill-mobile hit-pill-mobile--gray">
              {salaryDisplay}
            </span>
            {props.lastSeenLabel ? (
              <span className="hit-pill-mobile hit-pill-mobile--gray-muted">
                <Icon name="clock" useMaskForColor className="hit-icon-pill-mobile" />
                {props.lastSeenLabel}
              </span>
            ) : null}
          </div>
          <div className="hit-card__actions-mobile">
            <div className="hit-card__donut-mobile">
              <MatchDonut
                score={typeof props.matchScore === 'number' && Number.isFinite(props.matchScore) ? props.matchScore : null}
                size={40}
                breakdown={(props.matchScoreData as MatchScoreBreakdown | undefined) ?? undefined}
                strings={props.staticHitStrings.matchDonut}
              />
            </div>
            <div className="hit-card__actions-mobile-inner">
              <div
                className={`hit-btn-save ${isSaved ? '' : 'hit-btn-save--unsaved'}`}
                style={saveButtonStyle}
                aria-hidden
              >
                {isSaved ? (
                  <Icon name="bookmark" variant="solid" useMaskForColor className="hit-icon-btn" style={{ color: saveIconColor }} />
                ) : (
                  <Icon name="bookmark" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                )}
              </div>
              <div
                className={`hit-btn-menu ${stateButtonState ? 'hit-btn-menu--with-state' : 'hit-btn-menu--compact'} ${stateButtonColorClasses}`}
                aria-hidden
              >
                {stateButtonState ? (() => {
                  return (
                    <>
                      <Icon name={stateButtonState.icon} variant="solid" useMaskForColor className={`hit-icon-btn ${stateIconColorClasses}`} />
                      <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                    </>
                  );
                })() : (
                  <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                )}
              </div>
              <Link href={props.jobUrl} className="hit-btn-details-mobile" style={detailsBtnStyle}>
                {detailsLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function HitCompactViewWithIntl(props: HitCompactViewProps): React.ReactElement {
  const intl = useIntl();
  const detailsBtnStyle = getDetailsButtonStyle(props);
  const salaryDisplay =
    props.salaryLabel ||
    intl.formatMessage({
      id: 'hit.salaryNotSpecified.card',
      defaultMessage: 'Salary not specified',
      description: 'Shown when salary is not specified in job card',
    });
  const { singleLocation } = props;

  return (
    <article
      id={props.articleId}
      className="job-listing hit-card"
      title={props.jobTitle}
      aria-labelledby={props.titleId}
      style={{
        ...props.leftBorderStyle,
        position: 'relative',
        contentVisibility: 'auto',
        containIntrinsicSize: '1px 150px',
        contain: 'layout style paint',
      }}
    >
      <div className="hit-card__logo-mobile">
        <CompanyLogo
          companyName={props.companyId || ''}
          alt={props.companyId || ''}
          isTyomarkkinatori={props.isTyomarkkinatori}
          size={56}
          loading={props.index < 3 ? 'eager' : 'lazy'}
        />
        {props.companyId ? (
          <span className="hit-card__company-mobile">
            <CompanyName
              name={props.companyNameDisplay ?? props.companyId}
              className="font-bold text-xs md:text-sm text-gray-700 dark:text-gray-300"
            />
          </span>
        ) : null}
      </div>

      <div className="hit-card__main">
        <div className="hit-card__mobile-col">
          <div className="hit-card__title-block">
            <h3 id={props.titleId} className="sr-only">
              {props.jobTitle}
            </h3>
            <Link href={props.jobUrl} className={`${COMPACT_TITLE_LINK_CLASS_MOBILE} mb-0.5`}>
              {fixMojibake(props.jobTitle)}
            </Link>
            {props.rawTitleSubtitle ? (
              <div className={COMPACT_TITLE_SUBTITLE_CLASS_MOBILE}>
                {fixMojibake(props.rawTitleSubtitle)}
              </div>
            ) : null}
          </div>

          <div className="hit-pills-grid">
            {singleLocation.display ? (
              <span className="hit-pill-mobile hit-pill-mobile--green">
                <Icon name="mapPin" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">{fixMojibake(singleLocation.display)}</span>
              </span>
            ) : null}
            {props.remoteness ? (
              <span className="hit-pill-mobile hit-pill-mobile--blue hit-pill-mobile--w-auto">
                <Icon name="wifi" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">{fixMojibake(props.remoteness)}</span>
              </span>
            ) : null}
            {props.jobType ? (
              <span className="hit-pill-mobile hit-pill-mobile--purple hit-pill-mobile--w-auto">
                <Icon name="briefcase" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">{fixMojibake(props.jobType)}</span>
              </span>
            ) : null}
            {props.normalisedRoleLevel ? (
              <span className="hit-pill-mobile--orange-md-only">
                <span className="hit-pill__center">{fixMojibake(props.normalisedRoleLevel)}</span>
              </span>
            ) : null}
            {props.requiredLanguages?.length ? (
              <span className="hit-pill-mobile hit-pill-mobile--indigo hit-pill-mobile--w-auto">
                <Icon name="globeAlt" useMaskForColor className="hit-icon-pill-mobile" />
                <span className="hit-pill__center">
                  {props.requiredLanguages.slice(0, 2).map((lang, idx) => (
                    <span key={lang + idx}>
                      {fixMojibake(lang)}
                      {idx < Math.min(props.requiredLanguages?.length ?? 0, 2) - 1 && ', '}
                    </span>
                  ))}
                  {(props.requiredLanguages?.length ?? 0) > 2 && ` +${(props.requiredLanguages?.length ?? 0) - 2}`}
                </span>
              </span>
            ) : null}
            <span className="hit-pill-mobile hit-pill-mobile--gray">
              {salaryDisplay}
            </span>
            {props.lastSeenLabel ? (
              <span className="hit-pill-mobile hit-pill-mobile--gray-muted">
                <Icon name="clock" useMaskForColor className="hit-icon-pill-mobile" />
                {props.lastSeenLabel}
              </span>
            ) : null}
          </div>

          <div className="hit-card__actions-mobile">
            <div className="hit-card__donut-mobile">
              {typeof props.matchScore === 'number' && Number.isFinite(props.matchScore) ? (
                <MatchDonut
                  score={props.matchScore}
                  size={40}
                  breakdown={(props.matchScoreData as MatchScoreBreakdown | undefined) ?? undefined}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700" aria-hidden />
              )}
            </div>
            <div className="hit-card__actions-mobile-inner">
              <div className="hit-btn-save hit-btn-save--unsaved" aria-hidden>
                <Icon name="bookmark" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
              </div>
              <div className="hit-btn-menu hit-btn-menu--compact hit-btn-menu--default" aria-hidden>
                <Icon name="ellipsisVertical" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
              </div>
              <Link href={props.jobUrl} className="hit-btn-details-mobile" style={detailsBtnStyle}>
                <FormattedMessage
                  id="hit.seeAd"
                  defaultMessage="Details"
                  description="Button label to view job details"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
